import { Box, Flex, Icon, Text, HStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { SFChatBubblesIconOutlined, HorizontalDotMenuIcon } from '@assets/icons';
import { formatDistanceToNow } from 'date-fns';
import pluralize from 'pluralize';
import Dropdown from '@components/general/dropdown.component';
import { setConversationArchived } from '@services/query-services/conversations.query-service';
import { useStyledToast } from '@util/hooks';

export default function ConversationCard({ conversation, onArchive, ...containerProps }) {
  const toast = useStyledToast();
  const navigate = useNavigate();
  const lastMessageTime = conversation?.updated_at || conversation?.created_at;
  const messageCount = conversation?.messages?.length || 0;
  const sourcesCount = conversation?.sources?.length || 0;
  const documentVersions = conversation?.artifacts?.length || 0;

  const handleArchiveConversation = async () => {
    try {
      await setConversationArchived(conversation?.id, true);
      toast({
        title: 'Conversation archived successfully',
        status: 'success'
      });
      // Refetch conversations to update the list
      if (onArchive) onArchive();
    } catch (error) {
      console.error('Error archiving conversation:', error);
      toast({
        title: 'Failed to archive conversation',
        status: 'error'
      });
    }
  };

  const menuItems = [
    {
      name: 'Archive Conversation',
      props: { color: 'critical.primary' },
      onClick: handleArchiveConversation
    }
  ];

  return (
    <Flex
      data-testid='conversation-card'
      gap={2}
      p={3}
      maxW='100%'
      bg='white'
      borderRadius='md'
      border='1px'
      borderColor='gray.200'
      cursor='pointer'
      onClick={() => navigate(`/explorer/${conversation?.id}`)}
      _hover={{ bg: 'gray.50' }}
      {...containerProps}
    >
      <Flex
        flex={1}
        gap={2}
      >
        <Icon
          as={SFChatBubblesIconOutlined}
          color='brandPrimary.onContainer'
          boxSize='15px'
          m={0.5}
        />
        <Box
          flex={1}
          minW={0}
        >
          <Flex
            align='flex-start'
            justify='space-between'
            mb={1}
          >
            <Text
              fontSize='md'
              fontWeight={600}
              noOfLines={1}
              flex='1'
              pr={2}
            >
              {conversation?.title || 'Untitled Conversation'}
            </Text>
            <Box
              data-testid='dropdown-wrapper'
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown
                variant='default'
                menuItems={menuItems}
                icon={<HorizontalDotMenuIcon size='18px' />}
                triggerButtonProps={{
                  variant: 'ghost',
                  color: 'surface.onSurfaceVariant',
                  h: '5'
                }}
              />
            </Box>
          </Flex>
          <HStack
            spacing={10}
            fontSize='sm'
            color='gray.600'
            flexWrap='wrap'
          >
            <Text>Last message {formatDistanceToNow(new Date(lastMessageTime))} ago</Text>
            <Text>
              {messageCount} {pluralize('Message', messageCount)}
            </Text>
            <Text>
              {sourcesCount} {pluralize('Source', sourcesCount)}
            </Text>
            <Text>
              {documentVersions} {pluralize('Document Version', documentVersions)}
            </Text>
          </HStack>
        </Box>
      </Flex>
    </Flex>
  );
}
